import { useMemo } from 'react';

import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';

import { RootState } from '@/redux/ducks';
import { useMerchantPermissions } from '@/hooks/useMerchantPermissions';
import { MerchantType, Permission, RouteId } from '@/types';
import { getMerchantType } from '@/utils/helper';
import { checkCurrentMerchantPermissions } from '@/utils/permissions';
import { flattenedPfbRoutes } from '@/router/pfbRoutes';
import TestModeService from '@/services/testMode.service';

const permissionMerchantTypeMapping = {
	[MerchantType.OFFLINE]: 'offline',
	[MerchantType.ONLINE]: 'online',
	[MerchantType.FASTAG]: 'fastag',
} as const;

const externalRoutePermissions: Partial<
	Record<RouteId, (state: RootState) => boolean>
> = {
	[RouteId.DEVELOPER_SETTINGS_PAGE]: (state: RootState) => {
		const featureConfig = state.merchantProfile?.featureConfig;
		return featureConfig?.developerSettingConfig?.dashboardEnabled ?? true;
	},
};

/**
 * Performs below checks for requested routes for logged in merchant
 *  - permissions
 *  - allowed merchants
 *  - chimera access
 *	-	isSuperMerchant
 *	-	isUPIVerified
 *	-	isB2bPgActivated
 */

export const useAuthGuard = () => {
	const {
		merchantType,
		isB2bPgActivated: isB2bPgActivatedFlag,
		isMerchantUPIVerified: isMerchantUPIVerifiedFlag,
		isSuperMerchant: isSuperMerchantFlag,
		isSplitSettlementEnabled: isSplitSettlementEnabledFlag,
	} = useMerchantPermissions();
	const { data: chimeraConfigData = {} } = useSelector(
		(state: RootState) => state.userConfig.chimeraConfig,
	);
	const currentMerchantType: MerchantType = useMemo(
		() => getMerchantType(merchantType) as string as MerchantType,
		[merchantType],
	);
	const rootState = useSelector((state: RootState) => state);
	/**
	 * Checks if current logged in merchant has one of the required permission to
	 * access the requested route
	 *
	 * @param {Permission[]} permissions
	 * @returns {boolean}
	 */
	const checkMerchantHasPermission = (permissions?: Permission[]): boolean => {
		if (isEmpty(permissions)) return true;

		return Boolean(
			permissions?.some((per) => checkCurrentMerchantPermissions(per)),
		);
	};

	const getUserChimeraAccessForRoute = (
		chimeraAccess?: Record<string, boolean>,
	) => {
		if (isEmpty(chimeraAccess)) return true;

		return Object.entries(chimeraAccess).some(
			([chimeraKey, value]) => chimeraConfigData[chimeraKey] === value,
		);
	};

	const checkB2bPgActivated = (isB2bPgActivated?: boolean) => {
		if (isB2bPgActivated === undefined) return true;

		return isB2bPgActivatedFlag === isB2bPgActivated;
	};
	const checkMerchantUPIVerified = (isUPIVerified?: boolean) => {
		if (isUPIVerified === undefined) return true;

		return isMerchantUPIVerifiedFlag === isUPIVerified;
	};
	const checkSuperMerchant = (isSuperMerchant?: boolean) => {
		if (isSuperMerchant === undefined) return true;

		return isSuperMerchantFlag === isSuperMerchant;
	};

	const checkSplitSettlementEnabled = (isSplitSettlementEnabled?: boolean) => {
		if (isSplitSettlementEnabled === undefined) return true;

		return isSplitSettlementEnabledFlag === isSplitSettlementEnabled;
	};

	const checkExternalPermission = (routeId: RouteId) => {
		if (!externalRoutePermissions[routeId]) return true;
		return externalRoutePermissions[routeId]?.(rootState);
	};

	const checkTestModeSupport = (testModeSupported?: boolean) => {
		if (!TestModeService.isTestModeEnabled()) return true;
		return !!testModeSupported;
	};

	const shouldAccessRoute = (routeId: RouteId) => {
		const {
			allowedMerchants,
			permissions: routePermissions,
			routeData,
		} = flattenedPfbRoutes[routeId];

		if (!allowedMerchants.includes(currentMerchantType)) {
			return false;
		}

		const merchantCategory = permissionMerchantTypeMapping[currentMerchantType];
		const userPermissions = routePermissions[merchantCategory];
		const {
			isUPIVerified,
			isB2bPgActivated,
			isSuperMerchant,
			chimera,
			isSplitSettlementEnabled,
		} = routePermissions;

		const finalPermissions = Boolean(
			checkMerchantHasPermission(userPermissions) &&
				getUserChimeraAccessForRoute(chimera) &&
				checkB2bPgActivated(isB2bPgActivated) &&
				checkMerchantUPIVerified(isUPIVerified) &&
				checkSuperMerchant(isSuperMerchant) &&
				checkExternalPermission(routeId) &&
				checkSplitSettlementEnabled(isSplitSettlementEnabled) &&
				checkTestModeSupport(routeData?.testModeSupported),
		);

		return finalPermissions;
	};

	return { shouldAccessRoute };
};
